import React from "react";

import {
  Auth0Client,
  Auth0Config,
  VpAuth0Client,
  VpAuth0Config,
  VpAuth0Settings,
  AuthenticationSocialProvider
} from "@monorepo/libraries";
import { Auth0Callback, Auth0Error, Captcha, CatpchaConfiguration } from "auth0-js";
import { EmailPreference } from "./Authentication/Components/EmailPreferences";

declare global {
  interface Window {
    auth0Settings: string;
  }
}

const createAuth0Config = (): Auth0Config => {
  const auth0Settings =
    window.auth0Settings || (process.env.REACT_APP_AUTH0_CONFIG as string);
  const vpAuth0Setting = new VpAuth0Settings(auth0Settings);

  return new VpAuth0Config(vpAuth0Setting);
};

export const Auth0DefaultConfig: Auth0Config = createAuth0Config();

export interface Auth0Context {
  /**
   * Sends reset email to given email
   *
   * @param email delivery address to reset email
   */
  resetPassword: (email: string, callback: Auth0Callback<Auth0Error>) => void;

  /**
   * Authenticate user with Google OAuth
   * @param loginHint: pass the email address for google one tap
   */
  authenticateGoogle: (loginHint?: string) => void;

  /**
   * Authenticate user with Facebook OAuth
   */
  authenticateFacebook: () => void;

  /**
   * Authenticate user with Apple OAuth
   */
  authenticateApple: () => void;

  /**
   * Authenticate user
   *
   * @param email
   * @param password
   */
  login: (
    email: string,
    password: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ) => void;
  /**
   * Register new account for user.
   *
   * @param email
   * @param password
   * @param userMetadata
   */
  signUp: (
    email: string,
    password: string,
    name: string,
    surname: string,
    emailPreference: EmailPreference,
    signUpLocale: string,
    site: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ) => void;

  /**
   *
   * @param element
   * @param options
   * @param callback
   */
  renderCaptcha: (element: HTMLElement, options?: CatpchaConfiguration, callback?: Auth0Callback<any>) => Captcha;
}

export class VpSignAuth0Context implements Auth0Context {
  auth0Client: Auth0Client;

  constructor (auth0Config: Auth0Config) {
    this.auth0Client = new VpAuth0Client(auth0Config);
  }

  public resetPassword (
    email: string,
    callback: Auth0Callback<Auth0Error>
  ): void {
    this.auth0Client.changePassword(email, callback);
  }

  public authenticateGoogle (loginHint?: string): void {
    this.auth0Client.authorizeSocial(AuthenticationSocialProvider.Google, loginHint);
  }

  public authenticateFacebook (): void {
    this.auth0Client.authorizeSocial(AuthenticationSocialProvider.Facebook);
  }

  public authenticateApple (): void {
    this.auth0Client.authorizeSocial(AuthenticationSocialProvider.Apple);
  }

  public renderCaptcha (element: HTMLElement, options?: CatpchaConfiguration, callback?: Auth0Callback<any>): Captcha {
    return this.auth0Client.renderCaptcha(element, options, callback);
  }

  public login (
    email: string,
    password: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ): void {
    this.auth0Client.login(email, password, callback, captcha);
  }

  public signUp (
    email: string,
    password: string,
    name: string,
    surname: string,
    emailPreference: EmailPreference,
    signUpLocale: string,
    site: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ): void {
    this.auth0Client.signUp(
      email,
      password,
      name,
      surname,
      emailPreference === EmailPreference.All,
      emailPreference === EmailPreference.Fewer,
      signUpLocale,
      site,
      callback,
      captcha
    );
  }
}

export const SignAuth0Context = React.createContext<Auth0Context>(
  {} as Auth0Context
);
export const SignAuth0Provider = SignAuth0Context.Provider;
